import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, Clock, TrendingUp } from "lucide-react";

const AR_WEEKDAYS = ["أحد", "اثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];
const AR_MONTHS = [
  "يناير", "فبراير", "مارس", "إبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

const addWorkdays = (start, n) => {
  const d = new Date(start);
  d.setHours(0, 0, 0, 0);
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 5 && dow !== 6) added += 1;
  }
  return d;
};

const HeadAr = () => (
  <thead style={{ direction: "ltr" }}>
    <tr style={{ display: "flex", flexDirection: "row", direction: "ltr" }}>
      {AR_WEEKDAYS.map((w) => (
        <th
          key={w}
          scope="col"
          className="text-muted-foreground rounded-md font-normal text-[0.8rem]"
          style={{ width: "2rem", textAlign: "center" }}
        >
          {w}
        </th>
      ))}
    </tr>
  </thead>
);
const calendarFormatters = {
  formatCaption: (d) => `${AR_MONTHS[d.getMonth()]} ${d.getFullYear()}`,
};
const calendarComponents = { Head: HeadAr };

function formatHour(iso) {
  const d = new Date(iso);
  const h24 = d.getHours();
  const isPm = h24 >= 12;
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${String(h12).padStart(2, "0")}:00 ${isPm ? "مساءً" : "صباحاً"}`;
}

const sar = (v) => `${Number(v).toLocaleString("ar-SA")} ر.س`;

const fmtBookingDate = (iso) => {
  const d = new Date(iso);
  return `${AR_WEEKDAYS[d.getDay()]}، ${d.getDate()} ${AR_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const UpgradeBooking = () => {
  const { bookingId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [target, setTarget] = useState(null);
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fullDays, setFullDays] = useState(new Set());
  const [emptyDays, setEmptyDays] = useState(new Set());
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });

  useEffect(() => {
    if (!loading && !user) navigate(`/login?next=/upgrade/${bookingId}`);
  }, [user, loading, navigate, bookingId]);

  useEffect(() => {
    if (!user) return;
    api
      .get(`/bookings/${bookingId}/upgrade-options`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "تعذّر تحميل خيارات الترقية");
        navigate("/dashboard");
      });
  }, [user, bookingId, navigate]);

  const chooseTarget = (t) => {
    setTarget(t);
    setDate(undefined);
    setSelected(null);
    setSlots([]);
  };

  // Auto-select first plausible date for the chosen target (same heuristic
  // as the normal booking page; backend availability remains authoritative).
  useEffect(() => {
    if (!target || date) return;
    const now = new Date();
    let start;
    const lead = target.lead || {};
    if (lead.kind === "workdays") start = addWorkdays(now, lead.n);
    else if (lead.kind === "workweeks") start = addWorkdays(now, lead.n * 5);
    else if (lead.kind === "calendar_days") {
      start = new Date(now); start.setDate(start.getDate() + lead.n);
    } else if (lead.kind === "earliest_date") {
      const ed = new Date(lead.date + "T10:00:00");
      const plus24 = new Date(now); plus24.setDate(plus24.getDate() + 1);
      start = ed > plus24 ? ed : plus24;
    } else {
      start = new Date(now); start.setDate(start.getDate() + (target.is_immediate ? 2 : 1));
    }
    start.setHours(0, 0, 0, 0);
    const ALLOWED = new Set(target.is_immediate ? [0, 1, 2, 3, 4, 5, 6] : [0, 1, 2, 3, 4]);
    for (let i = 0; i < 30; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      if (ALLOWED.has(d.getDay())) {
        setDate(d);
        setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1));
        break;
      }
    }
  }, [target, date]);

  const dateStr = useMemo(() => {
    if (!date) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [date]);

  const dateLabel = useMemo(() => (date ? fmtBookingDate(date.toISOString()) : ""), [date]);

  useEffect(() => {
    if (!dateStr || !target) return;
    setLoadingSlots(true);
    setSelected(null);
    api
      .get("/availability", { params: { date_str: dateStr, service_id: target.id } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => toast.error("تعذّر تحميل الأوقات"))
      .finally(() => setLoadingSlots(false));
  }, [dateStr, target]);

  useEffect(() => {
    if (!target) return;
    if (target.is_immediate) {
      setFullDays(new Set());
      setEmptyDays(new Set());
      return;
    }
    api
      .get("/availability/month", {
        params: { year: viewMonth.getFullYear(), month: viewMonth.getMonth() + 1, service_id: target.id },
      })
      .then((r) => {
        setFullDays(new Set(r.data.full_days || []));
        setEmptyDays(new Set(r.data.empty_days || []));
      })
      .catch(() => {
        setFullDays(new Set());
        setEmptyDays(new Set());
      });
  }, [target, viewMonth]);

  const disabledDates = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    if (target?.is_immediate) return false;
    const dow = d.getDay();
    if (dow === 5 || dow === 6) return true;
    const lead = target?.lead || {};
    let minDate = null;
    if (lead.kind === "workdays") minDate = addWorkdays(today, lead.n);
    else if (lead.kind === "workweeks") minDate = addWorkdays(today, lead.n * 5);
    else if (lead.kind === "calendar_days") {
      minDate = new Date(today);
      minDate.setDate(minDate.getDate() + lead.n);
    } else if (lead.kind === "earliest_date") {
      minDate = new Date(lead.date + "T00:00:00");
    }
    if (minDate) {
      minDate.setHours(0, 0, 0, 0);
      if (d < minDate) return true;
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const key = `${y}-${m}-${day}`;
    if (fullDays.has(key) || emptyDays.has(key)) return true;
    return false;
  };

  const handleConfirm = async () => {
    if (!target || !selected) {
      toast.error("اختر الجلسة الجديدة وموعدًا متاحًا");
      return;
    }
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/bookings/${bookingId}/upgrade/checkout`, {
        target_service_id: target.id,
        target_slot_iso: selected,
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذّر بدء الدفع");
      setSubmitting(false);
    }
  };

  if (!user || !data) {
    return (
      <PageShell>
        <div className="p-12 text-center text-[#5E5C58]">جاري التحميل...</div>
      </PageShell>
    );
  }

  const b = data.booking;
  const targets = data.targets || [];

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-12" data-testid="upgrade-page">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-[#0A4D3C] inline-flex items-center gap-2 mb-6 hover:underline"
          data-testid="upgrade-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> العودة إلى حسابي
        </button>

        <h1 className="font-heading text-4xl text-[#222222] flex items-center gap-3">
          <TrendingUp className="w-8 h-8 text-[#0A4D3C]" /> ترقية الجلسة
        </h1>
        <p className="text-[#5E5C58] mt-2">
          اختاري جلسة أعلى فئة وادفعي فرق السعر فقط، مع اختيار موعد جديد حسب توفر الجلسة الجديدة.
        </p>

        <div className="mt-8 card-warm p-6" data-testid="upgrade-current-summary">
          <div className="text-sm text-[#5E5C58]">جلستك الحالية</div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-heading text-2xl text-[#222222]" data-testid="upgrade-current-service">
                {b.service_title_ar}
              </div>
              <div className="text-[#5E5C58] text-sm mt-1 flex items-center gap-4 flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="w-4 h-4" /> {fmtBookingDate(b.slot_iso)}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-4 h-4" /> {formatHour(b.slot_iso)}
                </span>
              </div>
            </div>
            <div className="font-heading text-2xl text-[#0A4D3C]" data-testid="upgrade-current-price">
              {sar(b.price)}
            </div>
          </div>
        </div>

        <h2 className="font-heading text-lg text-[#222222] mt-10">الترقية إلى:</h2>
        {targets.length === 0 ? (
          <p className="text-[#5E5C58] mt-3" data-testid="upgrade-no-targets">
            لا توجد جلسات أعلى فئة متاحة للترقية حاليًا.
          </p>
        ) : (
          <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {targets.map((t) => (
              <button
                key={t.id}
                data-testid={`upgrade-target-${t.id}`}
                onClick={() => chooseTarget(t)}
                className={`text-start card-warm p-5 border-2 transition-colors ${
                  target?.id === t.id
                    ? "border-[#0A4D3C] bg-[#F1F7F4]"
                    : "border-transparent hover:border-[#E5E1D8]"
                }`}
              >
                <div className="font-heading text-xl text-[#222222]">{t.title_ar}</div>
                <div className="mt-3 space-y-1 text-sm">
                  <div className="text-[#5E5C58]">
                    السعر الكامل: <span className="font-medium text-[#222222]">{sar(t.price)}</span>
                  </div>
                  <div className="text-[#0A4D3C] font-medium" data-testid={`upgrade-diff-${t.id}`}>
                    فرق الترقية: {sar(t.difference)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {target && (
          <div className="mt-10 grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 card-warm p-8">
              <h2 className="font-heading text-2xl text-[#222222]">
                اختاري الموعد الجديد — {target.title_ar}
              </h2>
              <p className="text-[#5E5C58] text-sm mt-1">
                المواعيد المعروضة حسب قواعد التوفر الفعلية لهذه الجلسة.
              </p>
              <div className="mt-6 grid md:grid-cols-2 gap-8">
                <div>
                  <div className="text-sm text-[#5E5C58] mb-3 flex items-center gap-2">
                    <CalendarDays className="w-4 h-4" /> اختر التاريخ
                  </div>
                  <div className="bg-[#F6F4EE] rounded-2xl p-2 inline-block" style={{ direction: "ltr" }}>
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={disabledDates}
                      weekStartsOn={0}
                      showOutsideDays={true}
                      formatters={calendarFormatters}
                      components={calendarComponents}
                      month={viewMonth}
                      onMonthChange={setViewMonth}
                      classNames={{ row: "flex w-full mt-2 flex-row" }}
                      dir="ltr"
                      data-testid="upgrade-calendar"
                    />
                  </div>
                </div>
                <div>
                  <div className="text-sm text-[#5E5C58] mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> الأوقات المتاحة
                  </div>
                  {!date && <p className="text-[#5E5C58] text-sm">يرجى اختيار التاريخ أولًا.</p>}
                  {date && loadingSlots && (
                    <p className="text-[#5E5C58] text-sm">جاري تحميل الأوقات...</p>
                  )}
                  {date && !loadingSlots && slots.length === 0 && (
                    <p className="text-[#B33939] text-sm" data-testid="upgrade-no-slots">
                      لا توجد أوقات متاحة في هذا اليوم.
                    </p>
                  )}
                  <div className="grid grid-cols-3 gap-2 mt-2" data-testid="upgrade-slots-grid">
                    {slots.map((iso) => (
                      <button
                        key={iso}
                        data-testid={`upgrade-slot-${iso}`}
                        onClick={() => setSelected(iso)}
                        className={`py-2 px-3 rounded-xl border text-sm transition-colors ${
                          selected === iso
                            ? "bg-[#0A4D3C] text-white border-[#0A4D3C]"
                            : "border-[#E5E1D8] bg-white hover:bg-[#F6F4EE]"
                        }`}
                      >
                        {formatHour(iso)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <aside className="card-warm p-8 h-fit sticky top-24">
              <div className="text-sm text-[#5E5C58]">فرق الترقية المستحق</div>
              <div className="font-heading text-4xl text-[#0A4D3C] mt-2" data-testid="upgrade-amount-due">
                {sar(target.difference)}
              </div>
              <ul className="mt-5 space-y-2 text-sm text-[#5E5C58]">
                <li>• سعر جلستك الحالية: {sar(b.price)}</li>
                <li>• سعر الجلسة الجديدة: {sar(target.price)}</li>
                <li>• يبقى موعدك الحالي محفوظًا حتى إتمام الدفع بنجاح</li>
              </ul>
              {selected && (
                <div
                  data-testid="upgrade-final-review"
                  className="mt-5 rounded-2xl border border-[#0A4D3C]/25 bg-[#F6F4EE] p-4"
                >
                  <div className="font-heading text-base text-[#0A4D3C]">مراجعة الترقية</div>
                  <ul className="mt-2 space-y-1 text-sm text-[#222222]">
                    <li>
                      <span className="text-[#5E5C58]">الجلسة الجديدة: </span>
                      <span className="font-medium" data-testid="upgrade-review-service">{target.title_ar}</span>
                    </li>
                    <li>
                      <span className="text-[#5E5C58]">التاريخ: </span>
                      <span className="font-medium" data-testid="upgrade-review-date">{dateLabel}</span>
                    </li>
                    <li>
                      <span className="text-[#5E5C58]">الوقت: </span>
                      <span className="font-medium" data-testid="upgrade-review-time">{formatHour(selected)}</span>
                    </li>
                  </ul>
                </div>
              )}
              <Button
                data-testid="upgrade-confirm-btn"
                onClick={handleConfirm}
                disabled={!selected || submitting}
                className="mt-5 w-full rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white h-11"
              >
                {submitting ? "جاري التحويل..." : "متابعة دفع فرق الترقية"}
              </Button>
            </aside>
          </div>
        )}
      </section>
    </PageShell>
  );
};

export default UpgradeBooking;
